import { LCG, randomSample } from "@keybr/rand";
import { type ClassName } from "@keybr/widget";
import { clsx } from "clsx";
import { type FunctionComponent, memo, type ReactNode } from "react";

const colors: readonly string[] = [
  "#2c3e50",
  "#7f8c8d",
  "#436eee",
  "#009acd",
  "#b0e0e6",
  "#389e4b",
  "#bf3eff",
  "#ff69b4",
  "#c0392b",
  "#e67e22",
  "#ffb90f",
];

type ShapeProps = {
  readonly className?: ClassName;
  readonly width: number;
  readonly height: number;
  readonly color: string;
};

const Shape0 = memo(function Shape0({
  className,
  width,
  height,
  color,
}: ShapeProps) {
  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox="0 0 80 48"
    >
      <path
        fill="#050708"
        d="M14.2 31.64a8.3 8.3 0 016.4.66 8.28 8.28 0 01.98 13.88 8.32 8.32 0 01-4.86 1.68h-.24a8.3 8.3 0 01-2.28-16.23z"
      />
      <path
        fill="#323332"
        d="M9.82 39.64c0-2.98 2.1-5.8 4.9-6.75-.44.47-.99.82-1.4 1.31a9.67 9.67 0 00-2.03 8.65c.16.7.46 1.37.72 2.06a7.17 7.17 0 01-2.18-5.27zM16.3 36.48a3.25 3.25 0 01.9 6.43 3.24 3.24 0 01-.9-6.42z"
      />
      <path
        fill="#050708"
        d="M63.28 31.64a8.3 8.3 0 016.4.66 8.3 8.3 0 01-3.88 15.56h-.24a8.3 8.3 0 01-2.28-16.23z"
      />
      <path
        fill="#323332"
        d="M58.9 39.64c0-2.98 2.1-5.8 4.9-6.75-.44.47-.99.82-1.4 1.31a9.67 9.67 0 00-2.03 8.65c.16.7.47 1.37.73 2.06a7.17 7.17 0 01-2.19-5.27zM65.39 36.48a3.25 3.25 0 01.89 6.43 3.24 3.24 0 01-.9-6.42z"
      />
      <path
        fill={color}
        d="M77.62 25.45c-.86-2.58-1.98-3.44-4.81-4.38-2.83-.94-15.02-1.54-17.26-1.72-.77-1.2-3.17-7.04-4.55-9.78-1.37-2.75-3.86-4.3-6.26-4.12-2.4.17-14.77-.09-21.9.34-7.12.43-12.87 8.67-16.82 16.05A43.99 43.99 0 00.58 40s2.75-.02 5.15-.01c.61-2.05 1.6-5.57 5.9-5.69 4.82-.13 6.62 0 9.26.07 2.57.06 4.13 1.82 6.23 7.48 5.77-.03 21.57-.15 27.94-.16 1.22-6.02 2.26-8.64 7.62-10.22 3.85-1.14 9.53-.39 11.52 3.39.72 1.36 1.16 3.74 1.43 6.97.43.01.53.11.53.11s1.03-4.55 1.8-7.55.52-6.35-.34-8.93zM29.1 22.03c0 1.03-.16 2.7-2 2.7l-13.48-.06c-1.74 0-2.1-1.32-1.7-3.06.38-1.74 3.89-9.47 6.2-10.92 2.32-1.45 4.67-1.8 8.82-1.38 1.48.13 2.23 1.25 2.23 2.77 0 2-.07 8.92-.07 9.95zm22.6 2.67l-16.77.03c-1.51 0-2.48-1.45-2.48-2.51V11.79c0-1.38 1.1-2.64 2.45-2.64s8.11-.07 9.6-.07c1.47 0 3.56.71 4.43 2.61.87 1.9 3.55 8.34 4.1 9.98.54 1.64.47 3.03-1.33 3.03z"
      />
      <path
        fill="#ffffff"
        d="M23.6 7.05c-1.4.05-2.81.26-4.14.81a11.5 11.5 0 00-4.42 3.52A37.45 37.45 0 0011.9 16a73.44 73.44 0 00-2.65 4.9c1.08-1.52 2.1-3.06 3.14-4.59 1.04-1.52 2.07-3.05 3.23-4.45a12.45 12.45 0 014.07-3.48c1.61-.8 3.46-1.08 5.3-1.32-.47-.02-.93-.02-1.4 0zm41.95 16.89c-6.68-.03-10.3.92-12.52 1.65l23.48.01c.75 0-.22-1-3.19-1.27a89.87 89.87 0 00-7.77-.4zm-2.77 4.84a9.32 9.32 0 00-5.2 1.68 8.62 8.62 0 00-3.45 7.7c.21-1.42.73-2.72 1.4-3.9a9.74 9.74 0 012.64-3 9.76 9.76 0 013.65-1.64c1.33-.3 2.73-.4 4.15-.38a11.1 11.1 0 00-3.2-.46zM16.73 31.5c-1.78.02-3.55.03-5.34.12-.89.05-1.82.13-2.7.48a5.3 5.3 0 00-2.1 1.72 5.79 5.79 0 012.26-1.26c.81-.2 1.68-.19 2.56-.17 1.76.07 3.54.1 5.32.1l2.66-.02c.9-.02 1.75-.08 2.58.05.84.12 1.66.38 2.42.78.75.4 1.52.9 1.92 1.68a2.47 2.47 0 00-.65-1.18c-.3-.34-.64-.65-1.02-.92a7.15 7.15 0 00-2.52-1.13c-.92-.23-1.86-.18-2.73-.22l-2.66-.03z"
      />
      <path
        fill="#d7eef4"
        d="M44.49 9.08c-1.48 0-8.24.07-9.6.07-1.34 0-2.44 1.25-2.44 2.64v10.43c0 1.06.97 2.51 2.48 2.51l16.77-.03c1.8 0 1.87-1.39 1.32-3.03-.54-1.64-3.22-8.08-4.09-9.98-.87-1.9-2.96-2.6-4.44-2.6zm-20.35.07c-2.52-.01-4.28.46-6.02 1.54-2.31 1.45-5.82 9.18-6.2 10.92-.4 1.74-.04 3.06 1.7 3.06l13.49.06c1.83 0 2-1.67 2-2.7 0-1.03.06-7.96.06-9.95 0-1.52-.74-2.64-2.22-2.77-1.04-.1-1.97-.16-2.8-.16z"
      />
      <path
        fill="#b5b5b6"
        d="M77.83 25.56c-.51 0-.92.47-.92 1.05v2.75c0 .57.41 1.04.92 1.04h1.25c.5 0 .92-.47.92-1.04V26.6c0-.58-.41-1.05-.92-1.05h-1.25zm-75.14.95c-.5 0-.92.47-.92 1.04v2.76c0 .57.41 1.04.92 1.04h1.25c.5 0 .92-.47.92-1.04v-2.76c0-.57-.41-1.04-.92-1.04H2.69zM1.3 32.6c-.71 0-1.29.57-1.29 1.28v3.42c0 .7.58 1.28 1.29 1.28h1.74c.7 0 1.28-.57 1.28-1.28v-3.42c0-.7-.57-1.28-1.28-1.28H1.29zm75.36 2.12c-.71 0-1.29.58-1.29 1.29v3.41c0 .71.58 1.29 1.3 1.29h1.73c.71 0 1.29-.58 1.29-1.29V36c0-.71-.58-1.29-1.3-1.29h-1.73z"
      />
      <path
        fill="#ffffff"
        d="M78.19 26.6c.29 0 .52.27.52.6v1.57c0 .32-.23.59-.52.59h.62c.29 0 .52-.27.52-.6V27.2c0-.33-.23-.6-.52-.6h-.62zm-75.23.95c-.29 0-.52.27-.52.6v1.56c0 .33.23.6.52.6h.62c-.29 0-.52-.27-.52-.6v-1.56c0-.33.23-.6.52-.6h-.62zm-1.36 6.1a.83.83 0 00-.83.83v2.2c0 .45.37.82.83.82h.64a.83.83 0 01-.82-.83v-2.19c0-.45.37-.82.82-.82H1.6zm75.84 2.13c.45 0 .82.37.82.83v2.19c0 .45-.37.83-.82.83h.64c.46 0 .83-.38.83-.83v-2.2a.83.83 0 00-.83-.82h-.64z"
      />
      <path
        fill="#323332"
        d="M74.2 34.85c-1.99-3.78-7.67-4.53-11.52-3.4-4.89 1.45-6.18 3.77-7.3 8.72H26.46c-1.78-4.33-3.27-5.76-5.57-5.81-2.64-.07-4.44-.2-9.27-.07-4.3.12-5.28 3.64-5.9 5.69l1.5.01c1.16-3.36 2.59-4.48 5.11-4.38 1.45.06 2.96.06 7.43.06 4.46 0 4.98 4.63 5.58 6.18h31.34l.06-.29c.74-4.27 1.64-5.96 3.33-7.29a8.73 8.73 0 015.65-1.86c5.28 0 6.75 2.48 7.6 3.94.86 1.46.7 4.47 1.12 5.58.55-.1.93-.12 1.19-.1-.27-3.24-.7-5.62-1.42-6.98z"
      />
    </svg>
  );
});

const Shape1 = memo(function Shape1({
  className,
  width,
  height,
  color,
}: ShapeProps) {
  return (
    <svg
      className={clsx(className)}
      width={width}
      height={height}
      viewBox="0 0 80 48"
    >
      <path
        fill={color}
        d="M78.06 27.39c-4.5-4.12-15.43-5.6-17.62-5.84l-.01-.06a7.46 7.46 0 00-3.66 1.3c-1.1.68-2.22-.24-2.65-1.21-.42-.98-3.06-8.31-3.31-9.16-.25-.85.22-1.45 1.14-1.6.8-.13 2.58-.24 3.5-.45a3.67 3.67 0 00-.1-.15c-.55-.82-2.3-2.22-5.64-2.97a58.7 58.7 0 00-17.9-1.12c-5.88.54-9.83 2.23-11.56 4.73.5-.03 1.12 0 1.76.16.88.23 1.65.53 1.75.98.21.95-1.07 5.2-1.25 5.68-.17.48-.77 2.5-2.47 2.97-1.57.44-2.87.1-3.82-.14-.59.2-1.16.42-1.71.7-.62-.38-1.3-.83-2.07-1.4-2.28-1.66-3.05-2.4-3.77-3.46 3.33.1 5.71-.5 6.32-1.9a214.8 214.8 0 00-14.96-1.1C-.26 15.5 1.46 16.96 4 16.9a21.84 21.84 0 006.3 8.78 15.92 15.92 0 00-1.09 6.27c0 3.99.85 10.33 2.7 10.33l16.09.77 28.95.05s15 .09 16.16 0c1.75-.12 2.05-.95 2.7-1.7 2.1-2.41 3.14-7.6 3.5-9.13.42-1.84.77-3.1-1.25-4.9zm-31.34-3.64c-2.75-.03-15.1-.43-16.1-.48-1-.05-3.67-.3-3.6-2.3.07-1.66.43-6.16.46-7.1.02-.95.8-2.03 2.24-2.1 1.45-.07 6.49-.2 6.49-.2 1.6 0 7.78-.12 8.5-.12.73 0 1.75.4 2.2 1.94a207.2 207.2 0 012.02 8c.13.8.55 2.38-2.21 2.36z"
      />
      <path
        fill="#050708"
        d="M62.35 30.75a8.75 8.75 0 016.74.7 8.75 8.75 0 01-4.1 16.4h-.24a8.76 8.76 0 01-2.4-17.11z"
      />
      <path
        fill="#323332"
        d="M57.73 39.23c0-3.14 2.2-6.12 5.16-7.11-.47.49-1.04.85-1.47 1.38a10.2 10.2 0 00-2.14 9.1c.16.77.48 1.47.76 2.19a7.56 7.56 0 01-2.3-5.56zm6.83-3.33a3.43 3.43 0 01.94 6.78 3.42 3.42 0 01-.94-6.77z"
      />
      <path
        fill="#050708"
        d="M17.56 30.75a8.75 8.75 0 016.75.7 8.74 8.74 0 01-4.1 16.4h-.24a8.76 8.76 0 01-2.4-17.11z"
      />
      <path
        fill="#323332"
        d="M12.95 39.23c0-3.14 2.2-6.12 5.16-7.11-.47.49-1.04.85-1.47 1.38a10.2 10.2 0 00-2.15 9.1c.17.77.5 1.47.77 2.19a7.57 7.57 0 01-2.3-5.56zm6.83-3.33a3.43 3.43 0 01.94 6.78 3.42 3.42 0 01-.94-6.77z"
      />
      <path
        fill="#d7eef4"
        d="M20.04 20.72c1.7-.47 2.3-2.5 2.47-2.97.17-.47 1.46-4.73 1.25-5.7-.1-.44-.88-.74-1.75-.96a5.68 5.68 0 00-1.77-.16c-.2.3-.38.6-.53.93-.97 2.18-2.5 5.5-3.46 8.7l-.03.02c.95.24 2.25.58 3.82.14zm30.76-8.24c.26.86 2.9 8.2 3.33 9.17.42.97 1.54 1.9 2.64 1.22a7.36 7.36 0 013.66-1.31c-.6-3.6-4.24-9.92-4.99-11.12-.9.2-2.68.32-3.48.45-.93.15-1.4.75-1.15 1.6zm-1.86 9c-.1-.56-1.58-6.47-2.03-8.02-.44-1.54-1.47-1.94-2.2-1.94-.72 0-6.9.12-8.5.12 0 0-5.04.12-6.49.2-1.44.07-2.22 1.15-2.24 2.1-.03.94-.4 5.44-.45 7.1-.02.35.06.65.19.9.62 1.18 2.58 1.36 3.4 1.4 1 .05 13.35.45 16.1.48 1.42 0 2-.4 2.2-.9.2-.5.08-1.06.02-1.45z"
      />
      <path
        fill="#e6e6e6"
        d="M79.62 31.38a11.67 11.67 0 01-3.25.03 7.08 7.08 0 01-3-1.12 7 7 0 003.03.7 9.57 9.57 0 003.05-.44.43.43 0 11.19.83zm-.44 2.3a27.2 27.2 0 01-2.42-.29 7.26 7.26 0 01-2.32-.75c.76.27 1.56.32 2.36.32.79 0 1.58-.07 2.38-.14a.43.43 0 110 .85zm-.33 1.88a13.43 13.43 0 01-2.08-.38 9.93 9.93 0 01-1.97-.69 10 10 0 002.03.27 15.82 15.82 0 002.03-.05c.24-.03.45.15.47.38a.43.43 0 01-.47.47zm-.79 2.02a11.9 11.9 0 01-2.8-1.3c.97.33 1.96.4 2.98.47a.43.43 0 11-.17.84z"
      />
      <path
        fill="#ffffff"
        d="M76.97 27.6a3.52 1.2 18.72 01-3.72 0 3.52 1.2 18.72 01-2.94-2.26 3.52 1.2 18.72 013.71 0 3.52 1.2 18.72 012.95 2.26zM69.52 29.48a9.48 9.48 0 00-2.6-.6c-.88-.11-1.77-.13-2.64-.05-1.75.16-3.46.66-4.88 1.63a9.46 9.46 0 00-3.27 3.94 19.4 19.4 0 00-1.43 5.13 13.73 13.73 0 01.83-5.4 9.08 9.08 0 013.4-4.38 9.68 9.68 0 015.31-1.57c1.85 0 3.73.33 5.28 1.3zm-40.56 3.5a6.75 6.75 0 00-1.59-2.22 10.75 10.75 0 00-7.32-2.83A9.97 9.97 0 0015 29.42c-.77.46-1.48 1.03-2.13 1.68a17.5 17.5 0 00-1.82 2.08 11.6 11.6 0 013.58-4.3 9.43 9.43 0 015.4-1.8 10.12 10.12 0 017.65 3.41 6.2 6.2 0 011.28 2.5zm.87 6.84a172.64 172.64 0 0111.87-.43c1.97.01 3.95.02 5.93.1l2.97.12c.98.07 1.97.11 2.96.2-.99.1-1.98.15-2.96.22l-2.97.12a196 196 0 01-5.93.1c-1.98-.02-3.96-.03-5.94-.1-1.97-.07-3.95-.16-5.93-.33zM1.4 13.98l12.59.86-12.61.25a.56.56 0 11.03-1.11zm25.45-5.65a51.87 51.87 0 0118.88-.71c2.1.32 4.18.77 6.2 1.55a.43.43 0 11-.33.8v-.01c-1.9-.8-3.93-1.33-5.98-1.71a51.79 51.79 0 00-18.77.09z"
      />
    </svg>
  );
});

const Shape2 = memo(function Shape2({
  className,
  width,
  height,
  color,
}: ShapeProps) {
  return (
    <svg
      className={clsx(className)}
      width={width}
      height={height}
      viewBox="0 0 80 48"
    >
      <path
        fill="#050708"
        d="M13.82 30.38a9.98 9.98 0 017.7.8 9.96 9.96 0 014.8 11.26 9.96 9.96 0 01-9.48 7.45h-.28A10 10 0 017.9 35.3a10.03 10.03 0 015.92-4.92z"
      />
      <path
        fill="#323332"
        d="M8.56 40a8.8 8.8 0 015.89-8.1c-.54.55-1.2.97-1.68 1.56a11.63 11.63 0 00-2.45 10.4c.19.86.56 1.66.87 2.48A8.62 8.62 0 018.57 40h-.01zM16.35 36.2a3.91 3.91 0 011.07 7.74 3.9 3.9 0 01-1.07-7.72z"
      />
      <path
        fill="#050708"
        d="M55.26 30.38a9.95 9.95 0 0112.5 12.05 9.96 9.96 0 01-9.47 7.46H58a10 10 0 01-8.66-14.59 10.03 10.03 0 015.91-4.92z"
      />
      <path
        fill="#323332"
        d="M50 40a8.8 8.8 0 015.89-8.1c-.54.55-1.2.97-1.68 1.56a11.63 11.63 0 00-2.45 10.4c.19.86.56 1.66.87 2.48A8.63 8.63 0 0150.01 40H50zM57.79 36.2a3.91 3.91 0 011.07 7.74 3.9 3.9 0 01-1.07-7.72z"
      />
      <path
        fill="#d5f6ff"
        d="M48.66 10.73c1.3.86 9.93 7.26 12.09 10.93 2.47-.55 2.65-.64 3.35-1.28-.9-1.1-3.29-3.55-5.6-5.41a108.86 108.86 0 00-8.69-6.29c.03.72-.33 1.27-1.15 2.05z"
      />
      <path
        fill={color}
        d="M77.22 30.07C73.98 22.23 66 20.44 64.2 20.3l-.1.09c-.7.64-.88.73-3.35 1.28-2.16-3.67-10.8-10.07-12.09-10.93.83-.78 1.18-1.33 1.15-2.05v-.13c-.06-.52-1.36-1.13-1.86.02-.25.55-.46.9-1.14 1.4-.64.5-.5 1.44.22 2.11 1.1 1.06 3.26 3.13 5.23 4.83-.57.65-1 1-1.87 1.94 0 0-28.98.08-30.5 0-.5-.72-2.8-3.02-3.66-3.88-1.59-.07-1.8.5-2.45.94-.8-.72-.65-.5-1.51-1.08-1.08.72-1.87 2.37-2.16 4.67-2.44 1.44-3.38 4.39-3.52 5.68C3.57 26.84.9 31.73.1 36.18c-.79 4.47 2.74 7.42 3.67 7.49.94.07 3.1-1.37 3.53-2.38-.29-1-.72-11.22 9.2-11.14 9.93.07 10.07 11.43 10.43 13.8l21.65-.07c0-.93-1.91-9.16 4.49-12.54C59.09 28.17 64 31.02 66.3 34c1.67 2.16 2.16 6.3 2.13 9.02 2.38 0 6.55.8 9.5-1.73 3.77-3.22 1.65-8.77-.72-11.22z"
      />
      <path
        fill="#ffffff"
        d="M12.19 15.53a.2.2 0 00-.12.06 3.61 3.61 0 00-.91 2.1s0 .02.02.02l.03-.01c.2-.65.6-1.21 1.04-1.67 1.6 1.12 3.3 2.07 4.93 3.1l2.56 1.6c.84.52 1.7 1.04 2.52 1.61h.04v-.04c-1.61-1.2-3.28-2.32-4.94-3.44-1.67-1.1-3.3-2.29-5.02-3.3h-.02a.2.2 0 00-.13-.03zM52.4 16.9c1.16 1.2 2.42 2.35 3.31 3.7.1.17.2.34.26.5.03.08.04.16.03.21a.9.9 0 01-.06.24c-.1.2-.59.27-.97.3-.8.08-1.65.08-2.48.09H27.42l-5.01.21 5.01.22H52.5c.84 0 1.68 0 2.53-.09.21-.02.43-.05.65-.1.2-.06.5-.14.65-.43.07-.14.09-.25.11-.36.03-.17 0-.3-.04-.42-.07-.23-.2-.42-.31-.6C55 19 53.66 18 52.4 16.9zm8.3 6.35c-.45 0-.9.02-1.35.05-1.8.1-3.6.4-5.3 1.02a12.9 12.9 0 00-4.62 2.8 13.14 13.14 0 00-3 4.45c-1.34 3.2-1.62 6.67-1.57 10.07H29.03v.87h15.9v-.87h-.06c.09-3.4.51-6.84 1.94-9.9a12.62 12.62 0 013.05-4.15 12.16 12.16 0 014.45-2.47c1.64-.51 3.36-.73 5.09-.77 1.73-.03 3.44.12 5.12.47a20.18 20.18 0 017.02 2.92 15.1 15.1 0 011.89 1.55l.02.03.03.02a.87.87 0 001.2-1.24c-.4-.38-.73-.64-1.11-.93a17.2 17.2 0 00-3.59-2.06 21.69 21.69 0 00-5.22-1.55c-1.35-.22-2.7-.32-4.06-.3zm-47.5.37c-.38 0-.74.02-1.1.05-.7.08-1.4.2-2.08.35-.7.17-1.35.34-2.09.67l-.05.02a.86.86 0 10.8 1.53h.02a9.83 9.83 0 011.72-.65 15.81 15.81 0 012.8-.52 18.12 18.12 0 012.89.15c2.56.35 5.04 1.35 7.1 2.97a14.85 14.85 0 014.6 6.3 18.3 18.3 0 011.15 7.87c.32-2.67.12-5.43-.74-8.02a15.5 15.5 0 00-4.46-6.81 15.7 15.7 0 00-7.44-3.58 18.55 18.55 0 00-3.12-.33z"
      />
      <path
        fill="#fcee21"
        d="M8.08 24.58A7.18 7.18 0 002.28 30s1.6.8 3.83-.27c1.34-.63 3.14-2.8 1.97-5.15zm63.4 1.53c-.39-.03-.7.05-.88.3-.93 1.29 1.94 5.53 4.03 5.46 1.36-.05 2.52-1.22 2.09-2.59-.26-.8-3.52-3.05-5.23-3.17zM20.72 15.26c-1.65 0-2.08 2.23-1.65 2.73.43.5 2.3 2.59 3.56 4 0 0 .1-2.56.1-3.64s-.35-3.1-2-3.1zm15.07.24c-1-.09-1.78.85-1.83 1.55-.07.94-.08 4.82-.08 4.82h4.68s-.43-1.08-1-2.08c.14-.58.71-2.59-.73-3.81-.36-.3-.71-.45-1.04-.48z"
      />
    </svg>
  );
});

const Shape3 = memo(function Shape3({
  className,
  width,
  height,
  color,
}: ShapeProps) {
  return (
    <svg
      className={clsx(className)}
      width={width}
      height={height}
      viewBox="0 0 80 48"
    >
      <path
        fill={color}
        d="M4.16 41C2.53 40.66.25 39.54.13 37.44c-.13-2.2-.59-3.79 1.6-6.69.94-1.25 1.14-1.4 1.6-1.89 0-2.03-.44-6.54 2.83-6.97 2.62-2.76 13-11.34 13.52-11.63-.07-.37-.51-1.02 1.23-1.16 1.75-.15 16.73-.63 21.15.29 14.68 3.05 26.1 11.99 31.32 15.11 5.24 3.13 6.54 11.63 6.62 13.38.07 1.74-1.46 4.8-3.13 5.08-2.58.2-8.84.65-19.44-.14-17.44-1.31-34.63.14-34.63.14z"
      />
      <path
        fill="#fbb03b"
        d="M3.33 28.4s1.55.86 1.81.47c.27-.4.6-3.2.23-4.25-.38-1.04-1.52-.83-1.52-.83s-.3.4-.45 1.9c-.13 1.31-.07 2.71-.07 2.71z"
      />
      <path
        fill="#ffffff"
        d="M69.46 26.44c1.67 0 4.44.4 5.23 1.27.8.87 1.38 2.1 1.6 4-1.92-3.46-6.83-5.27-6.83-5.27z"
      />
      <path
        fill="#d7eef4"
        d="M42.18 9.42c-.4.9.46.97 1.12 1.25.69.3 5.9 2.2 10.53 4.61 5.18 2.7 12.1 6.76 13.01 7.3.91.55 2.4 1.79 2.95 1.79.54 0 1.49.04 3.6.14C68.16 21.4 56.8 12.5 42.17 9.42zm-22.53.74c0 .04.02.08.03.11-.51.29-10.86 8.83-13.5 11.61 2.14.22 6.28.7 7.54.56 1.34-1.49 5.96-8.03 6.57-9.12.62-1.09 1.35-2.02.4-2.87-.25-.23-.57-.31-1.04-.3zm15.45.83c-3.02 0-7.17.08-9.1.08-2.58 0-2.8 1.02-3.42 1.85a210 210 0 00-4.57 7.6c-.59 1.01-1.28 2 0 1.96 1.27-.04 1.89 0 3.12 0 0 0 25.15.76 29.58.83 3.92.07 4.83-1.96 5.45-2.8.45-.6.55-1.15.04-1.52a63.42 63.42 0 00-9.01-5.21c-4.49-2.15-7.02-2.64-9.56-2.75A67.3 67.3 0 0035.1 11z"
      />
      <path
        fill="#fbb03b"
        d="M62.83 26.15h-2.94a.93.93 0 00-.4 1.76 13.3 13.3 0 013.68-1.7.9.9 0 00-.34-.06z"
      />
      <path
        fill="#ffffff"
        d="M6.03 22.06c.35.1.69.24 1.02.38.34.14.69.26 1.04.35.7.2 1.43.32 2.15.4 1.45.2 2.9.28 4.36.33 2.91 0 5.8.07 8.72.14l17.43.5 17.43.55 8.71.26 2.18.01h1.1a8.95 8.95 0 013.17.5c.67.26 1.34.6 1.8 1.16-.43-.6-1.1-.96-1.76-1.26a8.13 8.13 0 00-3.21-.68l-1.09-.05-2.18-.11-8.71-.27-17.43-.55-17.43-.5c-2.9-.06-5.82-.13-8.72-.13-1.45 0-2.9 0-4.35-.1-.72-.06-1.44-.15-2.14-.3a8.9 8.9 0 01-1.05-.3c-.34-.12-.68-.25-1.04-.33zm7.79 3.82A12.15 12.15 0 003.4 31.46c-1.19 1.85 3.67-4.1 10.38-4 6.72.1 11.23 5.63 9.81 3.61a12.14 12.14 0 00-9.78-5.2zm50.32.1c-1.52.11-3.02.6-4.38 1.35a10.2 10.2 0 00-5.26 7.38c-.25 1.53-.18 3.1.15 4.58.03-1.52.15-3.02.51-4.45a11.43 11.43 0 014.95-6.93 16.36 16.36 0 014.03-1.93z"
      />
      <path
        fill="#d7eef4"
        d="M28.7 23.59c-.19 0-.37.03-.55.08l.3 4.03c.08.01.17.03.26.03h.98a2.07 2.07 0 100-4.14h-.98zm-2.93 7.1c-.64 0-1.15.52-1.15 1.15l.74 2.57c.22.76.52 1.15 1.15 1.15h1.15c.64 0 1.15-.52 1.15-1.15 0 0-.57-1.92-.74-2.57-.16-.59-.52-1.15-1.15-1.15h-1.15zm5.13.07c-.63 0-1.14.51-1.14 1.15l.74 2.57c.18.66.51 1.15 1.15 1.15h16.28c.63 0 .87-.58 1.15-1.15.88-1.83 1.15-1.87 1.35-2.57.18-.61-.52-1.15-1.15-1.15H30.9zm22.37.36c-.64 0-.76.42-1.15 1.15l-1.42 2.57c0 .64.1 1.15 1.15 1.15h.65c.64 0 .7-.33 1.15-1.15l1.42-2.57c0-.63-.16-1.15-1.15-1.15h-.65z"
      />
      <path
        fill="#323332"
        d="M21.93 43.16l.67-2.35 3.87-.08c7.52-.3 18.9-.76 31.06.08l1.06 2.34c-12.04-.84-24.56-.54-32.03-.24z"
      />
      <path
        fill="#050708"
        d="M10.74 28.47a9.97 9.97 0 017.69.8 9.95 9.95 0 01-4.67 18.68h-.28a9.99 9.99 0 01-9.75-9.45v-.87c.05-1.47.43-2.93 1.1-4.25a10.02 10.02 0 015.91-4.92v.01z"
      />
      <path
        fill="#323332"
        d="M5.49 38.08a8.8 8.8 0 015.88-8.1c-.54.56-1.2.97-1.68 1.57a11.62 11.62 0 00-2.44 10.38c.18.86.55 1.66.86 2.47a8.62 8.62 0 01-2.62-6.32z"
      />
      <path
        fill="#323332"
        d="M12.97 32.13a6.08 6.08 0 011.67 12.03 6.06 6.06 0 01-1.67-12.01z"
      />
      <path
        fill="#050708"
        d="M13.27 34.29A3.9 3.9 0 0114.34 42a3.89 3.89 0 01-1.07-7.71zM63.87 28.47a9.97 9.97 0 017.68.8 9.96 9.96 0 01-4.66 18.68h-.28a9.98 9.98 0 01-8.65-14.57 10.03 10.03 0 015.91-4.92v.01z"
      />
      <path
        fill="#323332"
        d="M58.61 38.08a8.8 8.8 0 015.88-8.1c-.53.56-1.19.97-1.67 1.57a11.62 11.62 0 00-2.45 10.38c.19.86.56 1.66.87 2.47a8.6 8.6 0 01-2.62-6.32z"
      />
      <path
        fill="#323332"
        d="M66.1 32.13a6.09 6.09 0 016.85 5.69 6.11 6.11 0 01-5.19 6.34 6.06 6.06 0 01-1.67-12.01v-.02z"
      />
      <path
        fill="#050708"
        d="M66.4 34.29A3.9 3.9 0 0167.45 42a3.9 3.9 0 01-1.07-7.71z"
      />
      <path
        fill="#162d50"
        d="M48.58 39.79c-.1 0-.19 0-.3-.02-3.36-.35-13.5-.48-16.2-.22-.75.12-1.9.3-2.14-.77-.02-.1-2.1-10.25-2-15.11.09-4.62.52-12.5.53-12.58 0-.12.1-.22.23-.2.12 0 .2.1.2.22 0 .08-.43 7.96-.53 12.56-.1 4.82 1.98 14.92 2 15.02.1.5.51.61 1.66.43 2.72-.27 12.92-.14 16.3.22.69.07.7-.1.74-.4 1.57-8.03 5.2-10.14 7.37-11.4l.37-.22c1.88-1.12 2.63-1.9 2.84-2.96.02-.12.14-.2.25-.17.12.02.2.14.18.25-.24 1.21-1.04 2.06-3.05 3.26l-.37.21c-2.1 1.23-5.64 3.27-7.16 11.08-.06.56-.32.8-.92.8z"
      />
    </svg>
  );
});

const Shape4 = memo(function Shape4({
  className,
  width,
  height,
  color,
}: ShapeProps) {
  return (
    <svg
      className={clsx(className)}
      width={width}
      height={height}
      viewBox="0 0 80 48"
    >
      <path
        fill="#050708"
        d="M16.21 27.73c2.62-.8 5.56-.5 7.96.83a10.3 10.3 0 01-4.83 19.33h-.3a10.34 10.34 0 01-10.08-9.78v-.9c.05-1.52.44-3.04 1.14-4.4 1.26-2.42 3.5-4.3 6.11-5.09v.01z"
      />
      <path
        fill="#323332"
        d="M10.77 37.68a9.1 9.1 0 016.09-8.39c-.56.58-1.23 1.01-1.73 1.63a12.02 12.02 0 00-2.53 10.74c.19.89.57 1.72.9 2.56a8.91 8.91 0 01-2.72-6.54zM18.82 33.75a4.04 4.04 0 011.11 8 4.03 4.03 0 01-1.1-7.99z"
      />
      <path
        fill="#050708"
        d="M54.42 27.73c2.63-.8 5.56-.5 7.96.83a10.3 10.3 0 01-4.83 19.33h-.3a10.34 10.34 0 01-10.08-9.78v-.9c.05-1.52.44-3.04 1.14-4.4 1.26-2.42 3.5-4.3 6.12-5.09v.01z"
      />
      <path
        fill="#323332"
        d="M48.98 37.68a9.1 9.1 0 016.09-8.39c-.56.58-1.23 1.01-1.73 1.63a12.02 12.02 0 00-2.53 10.74c.19.89.57 1.72.9 2.56a8.91 8.91 0 01-2.72-6.54zM57.03 33.75a4.05 4.05 0 014.56 3.78 4.06 4.06 0 01-3.45 4.21 4.03 4.03 0 01-1.1-7.98z"
      />
      <path
        fill="#d7eef4"
        d="M6.67 14.9c0 .03 32.59-.1 54.08-.18h-4.17c0-4.32-1.7-8.87-4.91-11.73H21.2C10.13 3 6.67 9.31 6.67 14.91z"
      />
      <path
        fill={color}
        d="M19.15 24.16c7.18 0 13.2 4.48 14.74 10.46l8.48-.05c1.56-5.96 7.57-10.41 14.73-10.41 7.08 0 13.04 4.36 14.68 10.22l3.43-.02s-.17-5.63-.17-10.05c0-4.42-3.25-9.6-9.74-9.6h-4.55l-28.96.12V3h19.88A11.4 11.4 0 0043.93.1h-26.5C10.78.1 2.92 3.6 2.92 11.52s.17 12.94.17 23.3l1.27-.01c1.47-6.08 7.54-10.65 14.8-10.65zM21.2 2.99h6.15v11.86l-20.68.06c0-5.6 3.46-11.92 14.53-11.92z"
      />
      <path
        fill="#999999"
        d="M19.15 22.85a15.1 15.1 0 00-12.03 5.98h-2.5a4.62 4.62 0 100 9.24h1.9l.03-.1h1.57a11.04 11.04 0 0122.05 0h4.1v-.08h7.7v.09h4.1a11.04 11.04 0 0122.05 0h3.14l.02.09h4.1a4.61 4.61 0 100-9.24h-6.26a15.1 15.1 0 00-26.66 5.39h-8.68a15.14 15.14 0 00-14.63-11.37z"
      />
      <path
        fill="#ffffff"
        d="M19.15 24.9a12.8 12.8 0 018.88 4.2 12.28 12.28 0 013.17 8.88h2.05c-.04-.72-.14-1.43-.28-2.14h11.05v-1.36h-11.4a13.7 13.7 0 00-8.37-8.82c-1.65-.6-3.4-.84-5.1-.76zm37.95 0a12.8 12.8 0 018.88 4.2 12.3 12.3 0 013.17 8.88h2.05a13.95 13.95 0 00-4.5-9.61 13.45 13.45 0 00-9.6-3.47zM8.55 29.78l-2.1.17c-.7.07-1.38.06-2.15.23a4.07 4.07 0 00-2.98 2.94 3.77 3.77 0 013.1-2.26c.6-.04 1.34.07 2.03.11l2.1.17zm60.71 0v1.36l4.29-.17c.69-.04 1.42-.15 2.03-.11 1.28.1 2.54.96 3.1 2.26a4.07 4.07 0 00-2.98-2.94c-.77-.17-1.45-.16-2.15-.23z"
        opacity=".99"
      />
      <path
        fill="#ffffff"
        d="M42.02.8l-.9.02-6.9.13-13.79.25-3.45.06c-1.15.02-2.32.1-3.45.35a9.56 9.56 0 00-3.17 1.25c-.95.6-1.7 1.4-2.32 2.26a8.67 8.67 0 012.4-2.16 9.4 9.4 0 013.14-1.13c1.12-.2 2.26-.25 3.4-.23l3.45.06 13.8.2 6.9.1c1.91-.02 3.76.33 5.45 1v-.04h3.16a15.92 15.92 0 00-7.72-2.1zM49 4.22c1.23.83 2.26 1.86 3 3.04a9.83 9.83 0 011.42 5.49v.03c0 .5.47.91 1.04.9.57 0 1.02-.42 1.01-.93-.04-2.17-.62-4.4-1.92-6.3a11.26 11.26 0 00-2.02-2.23zm14.21 11.6c-3.08 0-6.07.04-9.11.05L35.92 16l-4.04.02v.55l4.04.02 18.17.17c3.02.04 6.1.04 9.06.1 1.44.09 2.9.37 4.25.82.6.2 1.17.45 1.7.74.31-.34.68-.6 1.08-.79l-.16-.1c-.7-.39-1.44-.67-2.19-.91-1.5-.46-3.03-.73-4.62-.8zm-35.77.22l-20.09.1v.27l20.09.13zM73.86 22.75a2.28 3.92 0 01-2.28 3.92 2.28 3.92 0 01-2.28-3.92 2.28 3.92 0 012.28-3.92 2.28 3.92 0 012.28 3.92z"
      />
    </svg>
  );
});

const Shape5 = memo(function Shape5({
  className,
  width,
  height,
  color,
}: ShapeProps) {
  return (
    <svg
      className={clsx(className)}
      width={width}
      height={height}
      viewBox="0 0 80 48"
    >
      <path
        fill={color}
        d="M5.294 38.132c-4.747 0-6.646-2.968-4.272-5.937.237-4.151.949-28.125 11.037-29.193C14.313.629 13.008.035 33.777.035s19.7 1.186 21.48 3.56c3.086 1.068 5.578 1.306 9.257 17.09 6.053 0 12.579-.831 14.006 12.461 2.254 1.188 2.254 5.578-2.256 5.695-5.816.121-70.97-.709-70.97-.709z"
      />
      <path
        fill="#d5f6ff"
        d="M62.734 21.672a2.693 2.693 0 01-2.691 2.692H42.521a2.691 2.691 0 01-2.691-2.692V7.474a2.692 2.692 0 012.691-2.692h10.521c1.485 0 3.958-.052 5.776 5.104 1.424 4.034 3.916 11.786 3.916 11.786z"
      />
      <path
        fill="#ffffff"
        d="M4.227 26.207c-.852 0-1.543.612-1.543 1.367v2.842c0 .757.69 1.37 1.543 1.37.85 0 1.543-.613 1.543-1.37v-2.842c0-.755-.691-1.367-1.543-1.367zM74.9 28.119c-.82 0-1.482.497-1.482 1.106v3.884c0 .61.662 1.104 1.482 1.104.818 0 1.483-.495 1.483-1.104v-3.884c0-.61-.665-1.106-1.483-1.106zM14.742 3.477c-.747.005-1.537.07-2.293.302a6.178 6.178 0 00-2.08 1.106c-1.227.987-2.064 2.306-2.717 3.648A21.455 21.455 0 006.6 12.852c.567-1.354 1.147-2.697 1.906-3.907.617-1.268 1.397-2.469 2.457-3.318 1.054-.87 2.379-1.221 3.79-1.201 1.438 0 2.906.043 4.36.07l8.752.182 4.38.076c1.46-.14 2.922-.248 4.38-.447-1.458-.2-2.914-.335-4.37-.512l-4.372-.066-8.748-.182c-1.462-.03-2.912-.07-4.393-.07zM68.328 22.37a12.253 12.253 0 00-2.822.262c1.855.372 3.7.506 5.348 1.123a6.838 6.838 0 012.232 1.297c.656.577 1.188 1.31 1.674 2.127a5.054 5.054 0 00-1.147-2.635c-.635-.773-1.514-1.327-2.435-1.672a8.973 8.973 0 00-2.85-.502z"
      />
      <path
        fill="#050708"
        d="M60.117 29.15a9.63 9.63 0 017.424.771 9.644 9.644 0 013.895 4.024 9.515 9.515 0 011.055 4.47c0 .274-.01.537-.038.81a10.917 10.917 0 01-.272 1.553 9.605 9.605 0 01-3.5 5.24c-1.607 1.241-3.621 1.898-5.645 1.946h-.271c-2.023-.057-4.027-.715-5.627-1.956-1.807-1.354-3.085-3.378-3.574-5.578a10.327 10.327 0 01-.217-1.592 13.257 13.257 0 010-.846 9.785 9.785 0 011.063-4.104c1.176-2.256 3.272-4.017 5.709-4.75l-.002.012z"
      />
      <path
        fill="#323332"
        d="M55.038 38.436c0-3.463 2.428-6.736 5.683-7.828-.519.537-1.147.94-1.619 1.517-2.397 2.672-3.207 6.567-2.36 10.028.18.826.536 1.599.838 2.389-1.618-1.57-2.597-3.829-2.531-6.105h-.011v-.001zM62.555 34.766c2.09-.348 4.205 1.401 4.252 3.527.16 1.893-1.354 3.689-3.217 3.934-2.089.366-4.215-1.4-4.253-3.519-.16-1.892 1.354-3.707 3.218-3.933v-.009z"
      />
      <path
        fill="#050708"
        d="M19.666 29.15a9.628 9.628 0 017.423.771 9.62 9.62 0 013.895 4.024 9.478 9.478 0 011.054 4.47c0 .274-.009.537-.038.81a9.614 9.614 0 01-3.772 6.793c-1.609 1.241-3.623 1.898-5.645 1.946h-.273c-2.023-.057-4.027-.715-5.626-1.956-1.806-1.354-3.085-3.378-3.575-5.578a10.086 10.086 0 01-.216-1.592 13.257 13.257 0 010-.846 9.77 9.77 0 011.063-4.104c1.176-2.256 3.273-4.017 5.709-4.75l.001.012z"
      />
      <path
        fill="#323332"
        d="M14.586 38.436c0-3.463 2.427-6.736 5.682-7.828-.518.537-1.148.94-1.619 1.517-2.398 2.672-3.208 6.567-2.361 10.028.179.826.536 1.599.838 2.389-1.619-1.57-2.597-3.829-2.531-6.105h-.009v-.001zM22.103 34.766c2.089-.348 4.206 1.401 4.252 3.527.16 1.893-1.355 3.689-3.217 3.934-2.088.366-4.214-1.4-4.252-3.519-.16-1.892 1.355-3.707 3.217-3.933v-.009z"
      />
    </svg>
  );
});

const shapes: readonly FunctionComponent<ShapeProps>[] = [
  Shape0,
  Shape1,
  Shape2,
  Shape3,
  Shape4,
  Shape5,
];

export type CarImageProps = {
  readonly className?: ClassName;
  readonly width?: number;
  readonly height?: number;
  readonly seed: number;
};

export const CarImage = memo(function CarImage({
  className,
  width = 80,
  height = 48,
  seed,
}: CarImageProps): ReactNode {
  const random = LCG(seed);
  const color = randomSample(colors, random);
  const Shape = randomSample(shapes, random);
  return (
    <Shape //
      className={className}
      width={width}
      height={height}
      color={color}
    />
  );
});
